import { z } from "zod";
import { defineStepConfig } from "../../registry/helpers";

/**
 * Pipeline Rows Insert Step
 *
 * Inserts parsed rows into the generic wfv2_pipeline_rows table,
 * namespaced by pipeline_id. Supports batch insertion, deduplication,
 * and basic validation rules.
 *
 * Example workflow:
 * ```
 * Parse File → Pipeline Rows Insert → Pipeline Rows Process
 * ```
 */
export const pipelineRowsInsertStep = defineStepConfig({
  type: "pipeline_rows_insert",
  name: "Insert Pipeline Rows",
  description:
    "Insert parsed rows into the pipeline rows table for downstream processing",
  category: "Data Access",

  inputSchema: z.object({
    rowsBlobKey: z
      .string()
      .describe("Scratch blob key containing parsed rows JSON"),
    containerName: z
      .string()
      .optional()
      .describe("Storage container (defaults to system default)"),
    sourceFilePath: z
      .string()
      .optional()
      .describe("Original source file path for traceability"),
    storageProfile: z
      .enum(["default", "ship_and_debit"])
      .optional()
      .describe("Storage profile to use (propagated from upstream)"),
  }),

  outputSchema: z.object({
    batchId: z.string().describe("UUID of the created batch"),
    pipelineId: z.string().describe("Pipeline identifier"),
    insertedCount: z.number().describe("Number of rows successfully inserted"),
    updatedCount: z
      .number()
      .describe(
        "Number of existing rows updated in-place (dedup update strategy)",
      ),
    skippedCount: z
      .number()
      .describe("Number of rows skipped due to validation or dedup"),
    filteredCount: z
      .number()
      .describe("Number of rows excluded by filterRules"),
    validationErrors: z
      .array(
        z.object({
          rowIndex: z.number(),
          errors: z.array(z.string()),
        }),
      )
      .describe("Validation errors for skipped rows"),
  }),

  configSchema: z.object({
    pipelineId: z
      .string()
      .describe("Logical pipeline identifier (e.g., 'daily_price_import')"),
    initialStatus: z
      .string()
      .prefault("pending")
      .describe("Initial status for all inserted rows"),
    batchSize: z
      .number()
      .prefault(500)
      .describe("Number of rows to insert per database batch"),
    deduplication: z
      .object({
        enabled: z.boolean().prefault(false),
        fields: z
          .array(z.string())
          .describe(
            "Row fields to use for dedup key (e.g., ['productId', 'date'])",
          ),
        strategy: z
          .enum(["skip", "update"])
          .prefault("skip")
          .describe("What to do when a duplicate is found"),
      })
      .optional()
      .describe("Deduplication configuration"),
    filterRules: z
      .array(
        z.object({
          conditions: z
            .array(
              z.object({
                field: z
                  .string()
                  .describe("Field path in row data (e.g., 'SRTYPE')"),
                operator: z.enum([
                  "eq",
                  "neq",
                  "gt",
                  "gte",
                  "lt",
                  "lte",
                  "contains",
                  "not_contains",
                  "starts_with",
                  "ends_with",
                  "is_null",
                  "is_not_null",
                  "in",
                  "not_in",
                  "regex_match",
                ]),
                value: z.unknown().optional(),
              }),
            )
            .describe("All conditions must match (AND logic)"),
        }),
      )
      .optional()
      .describe(
        "Row inclusion/exclusion filters using the same operators as pipeline_rows_process. " +
          "Controlled by filterMode.",
      ),
    filterMode: z
      .enum(["include", "exclude"])
      .prefault("include")
      .describe(
        "include: only rows matching ANY filter rule are kept. " +
          "exclude: rows matching ANY filter rule are skipped.",
      ),
    validationRules: z
      .array(
        z.object({
          field: z.string(),
          rule: z.enum([
            "required",
            "is_number",
            "is_date",
            "min_length",
            "max_length",
            "regex",
            "one_of",
          ]),
          params: z.record(z.string(), z.unknown()).optional(),
        }),
      )
      .optional()
      .describe("Validation rules to apply before insertion"),
    onValidationError: z
      .enum(["skip_row", "fail"])
      .prefault("skip_row")
      .describe("How to handle validation errors"),
  }),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "30 minutes",
    retry: {
      maximumAttempts: 2,
    },
  },
});
